/**
 * Local persistence for settings, translation history and session identity.
 *
 * Everything is namespaced under a versioned key so a schema change can be
 * introduced without a migration: a key that does not match the version is
 * discarded rather than migrated, which is the right trade for preferences.
 *
 * `localStorage` can throw (private browsing, disabled storage, quota), so
 * every access is guarded. Losing a preference is acceptable; crashing on boot
 * is not.
 */

import { STORAGE_KEYS } from "@shared/constants";
import type { LanguageCode } from "@shared/types/language";
import type { AppMode, AudioOutputMode } from "@shared/constants";
import type { PiperGender } from "./piper";

export interface Settings {
  readonly theme: "dark" | "light" | "system";
  readonly mode: AppMode;
  readonly sourceLanguage: LanguageCode | "auto";
  readonly targetLanguage: LanguageCode;
  readonly audioOutput: AudioOutputMode;
  readonly speakTranslations: boolean;
  readonly persistTranscript: boolean;
  readonly partialTranslation: boolean;
  readonly autoDetect: boolean;
  readonly showOriginal: boolean;
  readonly showTranslation: boolean;
  readonly subtitleSize: number;
  readonly subtitlePosition: "top" | "middle" | "bottom";
  readonly useBrowserAsr: boolean;
  readonly useBrowserTts: boolean;
  /** On-device Piper voices first — free, offline after the first download. */
  readonly usePiperTts: boolean;
  /** Preferred Piper voice gender; falls back with a note where unavailable. */
  readonly piperGender: PiperGender;
  /** Explicit Piper voice id override. Null means "resolve from language". */
  readonly piperVoiceId: string | null;
  readonly inputDeviceId: string | null;
  readonly historyEnabled: boolean;
  readonly lastSessionId: string | null;
}

export const DEFAULT_SETTINGS: Settings = {
  theme: "dark",
  mode: "voice",
  sourceLanguage: "auto",
  targetLanguage: "en",
  audioOutput: "off",
  // Finished utterances are read aloud in the target language by default: the
  // whole point of voice mode is hearing the translation, and local synthesis
  // (Piper, then the browser) costs nothing on the free plan.
  speakTranslations: true,
  persistTranscript: false,
  partialTranslation: true,
  autoDetect: true,
  showOriginal: true,
  showTranslation: true,
  subtitleSize: 1,
  subtitlePosition: "bottom",
  // The on-device paths cost nothing and are instant, so they lead by default.
  useBrowserAsr: true,
  useBrowserTts: true,
  usePiperTts: true,
  piperGender: "female",
  piperVoiceId: null,
  inputDeviceId: null,
  historyEnabled: true,
  lastSessionId: null,
};

export interface HistoryItem {
  readonly id: string;
  readonly sourceText: string;
  readonly translatedText: string;
  readonly sourceLanguage: LanguageCode | "auto";
  readonly targetLanguage: LanguageCode;
  readonly at: number;
  readonly provider: string;
}

const MAX_HISTORY = 200;

// ---------------------------------------------------------------------------

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    const parsed = JSON.parse(raw) as unknown;
    if (parsed === null || typeof parsed !== "object") return fallback;
    return { ...(fallback as object), ...(parsed as object) } as T;
  } catch {
    return fallback;
  }
}

function write(key: string, value: unknown): boolean {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    // Quota exceeded or storage disabled. The app keeps working in memory.
    return false;
  }
}

function remove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // Ignore.
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

// ---------------------------------------------------------------------------
//  Settings
// ---------------------------------------------------------------------------

export function loadSettings(): Settings {
  const stored = read<Partial<Settings>>(STORAGE_KEYS.settings, {});
  return sanitizeSettings({ ...DEFAULT_SETTINGS, ...stored });
}

/** Drops invalid enum values rather than trusting whatever is on disk. */
export function sanitizeSettings(input: Settings): Settings {
  const modes: readonly AppMode[] = ["text", "voice", "conversation", "subtitles"];
  const outputs: readonly AudioOutputMode[] = ["off", "translated", "both"];
  const positions = ["top", "middle", "bottom"] as const;
  const themes = ["dark", "light", "system"] as const;
  const genders: readonly PiperGender[] = ["female", "male"];

  return {
    ...input,
    theme: themes.includes(input.theme) ? input.theme : DEFAULT_SETTINGS.theme,
    mode: modes.includes(input.mode) ? input.mode : DEFAULT_SETTINGS.mode,
    audioOutput: outputs.includes(input.audioOutput) ? input.audioOutput : DEFAULT_SETTINGS.audioOutput,
    subtitlePosition: positions.includes(input.subtitlePosition) ? input.subtitlePosition : DEFAULT_SETTINGS.subtitlePosition,
    subtitleSize: clamp(Number.isFinite(input.subtitleSize) ? input.subtitleSize : 1, 0.6, 2.4),
    sourceLanguage: typeof input.sourceLanguage === "string" ? input.sourceLanguage : DEFAULT_SETTINGS.sourceLanguage,
    targetLanguage: typeof input.targetLanguage === "string" ? input.targetLanguage : DEFAULT_SETTINGS.targetLanguage,
    piperGender: genders.includes(input.piperGender) ? input.piperGender : DEFAULT_SETTINGS.piperGender,
    piperVoiceId: typeof input.piperVoiceId === "string" && input.piperVoiceId.trim() !== "" ? input.piperVoiceId.trim() : null,
    usePiperTts: typeof input.usePiperTts === "boolean" ? input.usePiperTts : DEFAULT_SETTINGS.usePiperTts,
    speakTranslations: typeof input.speakTranslations === "boolean" ? input.speakTranslations : DEFAULT_SETTINGS.speakTranslations,
  };
}

export function saveSettings(settings: Settings): boolean {
  return write(STORAGE_KEYS.settings, settings);
}

export function resetSettings(): Settings {
  remove(STORAGE_KEYS.settings);
  return { ...DEFAULT_SETTINGS };
}

// ---------------------------------------------------------------------------
//  History
// ---------------------------------------------------------------------------

export function loadHistory(): HistoryItem[] {
  const raw = read<unknown>(STORAGE_KEYS.history, []);
  if (!Array.isArray(raw)) return [];
  return raw
    .filter(isRecord)
    .filter((entry) => typeof entry["sourceText"] === "string" && typeof entry["translatedText"] === "string")
    .slice(0, MAX_HISTORY)
    .map((entry, index) => ({
      id: typeof entry["id"] === "string" ? entry["id"] : `h${index}_${Date.now()}`,
      sourceText: entry["sourceText"] as string,
      translatedText: entry["translatedText"] as string,
      sourceLanguage: (entry["sourceLanguage"] as LanguageCode) ?? "auto",
      targetLanguage: (entry["targetLanguage"] as LanguageCode) ?? "en",
      at: typeof entry["at"] === "number" ? entry["at"] : Date.now(),
      provider: typeof entry["provider"] === "string" ? entry["provider"] : "",
    }));
}

export function appendHistory(item: Omit<HistoryItem, "id" | "at">): HistoryItem[] {
  const entry: HistoryItem = { ...item, id: `h_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`, at: Date.now() };
  const next = [entry, ...loadHistory()].slice(0, MAX_HISTORY);
  write(STORAGE_KEYS.history, next);
  return next;
}

export function removeHistoryItem(id: string): HistoryItem[] {
  const next = loadHistory().filter((entry) => entry.id !== id);
  write(STORAGE_KEYS.history, next);
  return next;
}

export function clearHistory(): void {
  remove(STORAGE_KEYS.history);
}

// ---------------------------------------------------------------------------
//  Session identity
// ---------------------------------------------------------------------------

/**
 * The last session id is kept so a reload can offer to resume.
 *
 * The *token* is intentionally not persisted. It is a bearer credential, and
 * putting it in `localStorage` would make any XSS on the origin a full session
 * takeover. Resuming therefore requires re-authorising, which is the correct
 * trade.
 */
export function loadLastSessionId(): string | null {
  const stored = read<{ sessionId?: unknown }>(STORAGE_KEYS.session, {});
  return typeof stored.sessionId === "string" ? stored.sessionId : null;
}

export function saveLastSessionId(sessionId: string | null): void {
  if (sessionId === null) remove(STORAGE_KEYS.session);
  else write(STORAGE_KEYS.session, { sessionId, at: Date.now() });
}

const clamp = (value: number, min: number, max: number): number => Math.min(max, Math.max(min, value));
